import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import PanelLoading from "../../PanelLoading"
import PanelInfiniteScroll from "../PanelInfiniteScroll"
import ImportedArticle from "../../articles/ImportedArticle"
import RawArticle from "../../articles/RawArticle"
import EmptyResults from "../EmptyResults"
import {
	clearOneTimeSearchError,
	paginateOneTimeSearch,
	selectArticleSearchQuery,
	updateArticleAsImported,
	setArticles
} from "../../../reducers/articleSearchQuerySlice"
import { useSnackbar } from "notistack"
import {checkExclusion, getArticleTargetKey} from "../../../utils/articleUtilities"
import { selectAllUnassignedArticles } from "../../../reducers/unassignedArticlesEventsSlice"
import {
	setDeletedArticlesAsDeletedV2,
	setUnassignedArticlesAsImportedV2
} from "../../../utils/automatedSearchUtilities"
import { selectWorkspaceDeletedArticles } from "../../../reducers/deletedArticlesSlice"
import {useTabPanelScrollEl} from "../../panels/TabPanel"
import {selectWorkspaceExclusions} from "../../../reducers/workspaceSlice"
import {useWorkspaceContext} from "../../../pages/Dashboard"
import {useAllUnassignedArticleIds} from "../../../hooks/MemoizedSelectors"

export function SearchPanel() {
	const { enqueueSnackbar } = useSnackbar()
	const {workspaceId} = useWorkspaceContext()
	const unassignedArticleIds = useAllUnassignedArticleIds()
	const dispatch = useDispatch()
	const search = useSelector((state) => selectArticleSearchQuery(state, workspaceId))
	const workspaceExcludedArticles = useSelector((state) => selectWorkspaceExclusions(state, workspaceId))
	const unassignedArticles = useSelector((state) => selectAllUnassignedArticles(state, workspaceId))
	const deletedArticles = useSelector((state) => selectWorkspaceDeletedArticles(state, workspaceId))
	const [targetArticle, setTargetArticle] = useState(null)
	const scrollableTarget=useTabPanelScrollEl()

	const paginate = () => {
		dispatch(paginateOneTimeSearch({ workspaceId }))
	}

	useEffect(() => {
		const updatedArticles = setUnassignedArticlesAsImportedV2(unassignedArticles, search.articles)
		dispatch(setArticles({articles: updatedArticles, workspaceId}))
	}, [unassignedArticles])

	useEffect(() => {
		const updatedArticles = setDeletedArticlesAsDeletedV2(deletedArticles, search.articles)
		dispatch(setArticles({articles: updatedArticles, workspaceId}))
	}, [deletedArticles])

	useEffect(() => {
		if(search.error) {
			enqueueSnackbar(search.error, {variant: "error", autoHideDuration: 5000, onClose: dispatch(clearOneTimeSearchError(workspaceId))})
		}
	}, [search.error])

	const setArticleAsImported = (article) => dispatch(updateArticleAsImported(article))

	if (search.isLoading) {
		return <PanelLoading />
	}

	if (
		search.query &&
		search.source === "library" &&
		search.count === 0
	) {
		return (<EmptyResults title={"No Results Found"} body={"Currently, there are no search results in imported articles. Please try again in the future or try a new search."} />)
	}

	if (
		search.count == 0 &&
		!search.isLoading &&
		search.query
	) { 
		return (<EmptyResults title={"No Results Found"} body={"Currently, there are no search results for your query. Please try again in the future or try a new search."} />)
	}

	return (
		<PanelInfiniteScroll
			fitContent
			next={paginate}
			dataLength={search.articles.length}
			hasMore={search.query && search.hasMore}
			loader={<PanelLoading />}
			scrollableTarget={scrollableTarget.current}
		>
			{search.articles.map((result, index) => {
				if (result.articleId && result.deletedAt === null) {
					return (
						<ImportedArticle
							key={result.articleId}
							articleWorkspace={result}
							targetArticle={targetArticle}
							setTargetArticle={setTargetArticle}
							isUnassigned={unassignedArticleIds.includes(result.articleId)}
							index={index}
						/>
					)
				} else {
					let rawArticle = result.articleId ? result.article : result
					return (
						<RawArticle
							key={getArticleTargetKey(rawArticle)}
							workspaceId={workspaceId}
							article={rawArticle}
							targetArticle={targetArticle}
							setTargetArticle={setTargetArticle}
							setArticleAsImported={setArticleAsImported}
							isExcluded={checkExclusion(rawArticle.PMID, workspaceExcludedArticles)}
							index={index}
							deletedAt={result.deletedAt}
						/>
					)
				}
			})}
		</PanelInfiniteScroll>
	)
}
